import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import React from "react";

export type TooltipDirection =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "bottom-left"
  | "bottom-right";

export interface TooltipState {
  isVisible: boolean;
  content: string | null;
  direction: TooltipDirection;
  top: number;
  left: number;
  transform: string;
  tooltipContainerClass: string;
}

const initialState: TooltipState = {
  isVisible: false,
  content: null,
  direction: "top",
  top: 0,
  left: 0,
  transform: "",
  tooltipContainerClass: "",
};

export const tooltipSlice = createSlice({
  name: "tooltip",
  initialState,
  reducers: {
    showTooltip: {
      reducer(state, action: PayloadAction<TooltipState>) {
        return { ...action.payload, isVisible: true };
      },
      prepare(
        e: React.MouseEvent<HTMLElement>,
        content: string,
        direction: TooltipDirection = "top",
        tooltipContainerClass: string = ""
      ) {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        const offset = 24;
        let top: number, left: number, transform: string;

        switch (direction) {
          case "top":
            top = rect.top + window.scrollY - (offset - 12);
            left = rect.left + window.scrollX + rect.width / 2;
            transform = "translate(-50%, -100%)";
            break;
          case "bottom":
            top = rect.top + window.scrollY + rect.height + (offset - 12);
            left = rect.left + window.scrollX + rect.width / 2;
            transform = "translate(-50%, 0)";
            break;
          case "left":
            top = rect.top + window.scrollY + rect.height / 2;
            left = rect.left + window.scrollX - offset;
            transform = "translate(-100%, -50%)";
            break;
          case "right":
            top = rect.top + window.scrollY + rect.height / 2;
            left = rect.left + window.scrollX + rect.width + offset;
            transform = "translate(0, -50%)";
            break;
          case "bottom-left":
            top = rect.top + window.scrollY + rect.height + (offset - 12);
            left = rect.left + window.scrollX - offset;
            transform = "translate(10%, 0)";
            break;
          case "bottom-right":
            top = rect.top + window.scrollY + rect.height + (offset - 12);
            left = rect.left + window.scrollX + rect.width + offset;
            transform = "translate(-110%, 0)";
            break;
          default:
            top = rect.top + window.scrollY;
            left = rect.left + window.scrollX;
            transform = "";
            break;
        }
        return {
          payload: {
            isVisible: true,
            content,
            direction,
            top,
            left,
            transform,
            tooltipContainerClass,
          },
        };
      },
    },
    hideTooltip() {
      return { ...initialState };
    },
  },
});

export const { showTooltip, hideTooltip } = tooltipSlice.actions;
